/** @jsxImportSource @emotion/react */
import React from 'react'
import { CSSObject } from '@emotion/styled'
import estyled, { ecss } from '@signal/theme/emotion'
import { motion } from 'framer-motion'

/**
 * Wraps an emotion styled-component with framer-motion's motion().
 * Emotion styled-components lack the $$typeof that motion() expects,
 * so we cast through unknown to bridge the type gap.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const motionStyled = <P extends Record<string, unknown>>(
  component: React.ComponentType<P>
) =>
  motion(
    component as unknown as React.ForwardRefExoticComponent<
      P & React.RefAttributes<HTMLDivElement>
    >
  ) as unknown as React.FC<React.PropsWithChildren<P & Record<string, unknown>>>
import {
  getHeaderContentHeight,
  getMenuPanelWidth
} from '@signal/layouts/utils'
import {
  drawerZIndexSelector,
  getColorBackgroundSelector,
  getColorPrimarySelector,
  getColorTextSelector,
  getSpacingSelector,
  paletteDividerSelector as paletteDividerSelectorRaw
} from '@signal/theme/selectors'

const paletteDividerSelector = paletteDividerSelectorRaw as unknown as CSSObject

// Menu panel widths for different states
//export const MENU_PANEL_COLLAPSED_WIDTH = '60px'
//export const MENU_PANEL_EXPANDED_WIDTH = '240px'

//const getMenuPanelWidthFunc = ({ $getMenuPanelWidth }) => getMenuPanelWidth()

export const MENU_PADDING_SELECTOR = 1.2
export const MENU_PADDING_SELECTOR_SMALL = 0.5

interface MenuWrapperProps {
  $isExpanded?: boolean
  $menuPanelWidth?: string
}

export const OuterWrapper = estyled.div<MenuWrapperProps>`
  height: 100%;
  width: ${({ $menuPanelWidth }) => $menuPanelWidth || getMenuPanelWidth()};
  transition: width 0.3s ease-in-out;

  @media print {
    display: none;
  }
`

export const InnerWrapper = estyled.div<MenuWrapperProps>`
  position: fixed;
  height: 100%;
  width: ${({ $menuPanelWidth }) => $menuPanelWidth || getMenuPanelWidth()};
  z-index: calc(${drawerZIndexSelector} + 1);
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.1);
  background: ${getColorBackgroundSelector('paper')};
  border-style: solid;
  border-color: ${paletteDividerSelector};
  border-width: 0 1px 0 0;
  transition: width 0.3s ease-in-out;
`

export const LogoWrapper = estyled.div<MenuWrapperProps>`
  display: flex;
  align-items: center;
  justify-content: ${({ $isExpanded }) =>
    $isExpanded ? 'flex-start' : 'center'};
  height: ${getHeaderContentHeight(true /*hasHeader*/)};
  border-bottom: 1px solid ${paletteDividerSelector};
  user-select: none;
  padding: 0 ${getSpacingSelector(MENU_PADDING_SELECTOR)}px;
  transition: justify-content 0.3s ease-in-out;
`

export const MenuWrapper = estyled.div<MenuWrapperProps>`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`

export type ScrollSectionProps = {
  $isExpanded: boolean
}

export const ScrollSection = estyled.div<ScrollSectionProps>`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  gap: ${getSpacingSelector(MENU_PADDING_SELECTOR - 1)}px;
  padding: 0 ${getSpacingSelector(MENU_PADDING_SELECTOR)}px;
  padding-top: ${getSpacingSelector(MENU_PADDING_SELECTOR)}px;
  transition: padding 0.3s ease-in-out;

  ${({ $isExpanded }) =>
    !$isExpanded &&
    ecss`
      padding: 0;
      gap: 0;
    `}
`

export const BottomSection = estyled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-top: 1px solid ${paletteDividerSelector};
  padding: ${getSpacingSelector(MENU_PADDING_SELECTOR)}px;

  /* Add ellipsis to text content */
  > * {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: clip;
  }
`

export const ToggleButton = estyled.button`
  position: absolute;
  top: 50%;
  right: -12px;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid ${paletteDividerSelector};
  background: ${getColorBackgroundSelector('paper')};
  color: ${getColorTextSelector('primary')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  z-index: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease-in-out;

  &:hover {
    background: ${getColorBackgroundSelector('lightBlue')};
    color: ${getColorPrimarySelector()};
    transform: translateY(-50%) scale(1.05);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${getColorPrimarySelector('light')};
  }
`

export const MenuContent = estyled.div<MenuWrapperProps>`
  padding: 16px;
  color: ${getColorTextSelector('secondary')};
  font-size: 14px;
  text-align: ${({ $isExpanded }) => ($isExpanded ? 'left' : 'center')};
  transition: all 0.3s ease-in-out;

  ${({ $isExpanded }) =>
    !$isExpanded &&
    ecss`
      writing-mode: vertical-rl;
      text-orientation: mixed;
      padding: 16px 8px;
    `}
`

// Scroll overlay components
export const ScrollOverlayTop = estyled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 30px;
  background: linear-gradient(
    to bottom,
    ${getColorBackgroundSelector('paper')} 0%,
    transparent 100%
  );
  pointer-events: none;
  z-index: 10;
`

export const ScrollOverlayBottom = estyled.div`
  position: absolute;
  bottom: 60px; /* Account for BottomSection height */
  left: 0;
  right: 0;
  height: 30px;
  background: linear-gradient(
    to top,
    ${getColorBackgroundSelector('paper')} 0%,
    transparent 100%
  );
  pointer-events: none;
  z-index: 10;
`

// Scroll button component with position props
export type ScrollButtonProps = {
  $position: 'top' | 'bottom'
}

export const ScrollButton = estyled.button<ScrollButtonProps>`
  position: absolute;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 0;
  background: ${getColorBackgroundSelector('lightBlue')};
  color: ${getColorPrimarySelector()};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  z-index: 11;
  right: 8px;
  transition: transform 0.1s ease-in-out;

  /* Position-specific styles */
  ${({ $position }) =>
    $position === 'top' &&
    ecss`
      top: 8px;
    `}

  ${({ $position }) =>
    $position === 'bottom' &&
    ecss`
      bottom: 68px; /* Account for BottomSection height + margin */
    `}

  &:hover {
    background: ${getColorBackgroundSelector('lightBlue')};
    color: ${getColorPrimarySelector()};
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${getColorPrimarySelector()};
  }
`

export const SkeletonSearchWrapper = estyled.div`
  padding: ${getSpacingSelector(MENU_PADDING_SELECTOR)}px
    ${getSpacingSelector(MENU_PADDING_SELECTOR + 0.8)}px
    ${getSpacingSelector(MENU_PADDING_SELECTOR - 0.2)}px;
`

export const SkeletonMenuItems = estyled.div<MenuWrapperProps>`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${getSpacingSelector(2)}px;
  padding: 0 ${getSpacingSelector(MENU_PADDING_SELECTOR)}px;
  padding-top: ${getSpacingSelector(MENU_PADDING_SELECTOR)}px;

  ${({ $isExpanded }) =>
    !$isExpanded &&
    ecss`
      padding: 0;
      gap: 0;
    `}
`

export const SkeletonMenuRow = estyled.div<MenuWrapperProps>`
  display: flex;
  align-items: center;
  justify-content: ${({ $isExpanded }) =>
    $isExpanded ? 'flex-start' : 'center'};
  gap: ${getSpacingSelector(1)}px;
  min-height: 52px;
  padding: ${getSpacingSelector(MENU_PADDING_SELECTOR_SMALL)}px;
  border-radius: ${({ $isExpanded }) => ($isExpanded ? '30px' : '0')};

  ${({ $isExpanded }) =>
    !$isExpanded &&
    ecss`
      border-bottom: 1px solid ${paletteDividerSelector};
    `}
`

export const SkeletonBottomSection = estyled.div<MenuWrapperProps>`
  display: flex;
  flex-direction: column;
  gap: ${getSpacingSelector(1)}px;
  border-top: 1px solid ${paletteDividerSelector};
  padding: ${getSpacingSelector(MENU_PADDING_SELECTOR)}px;

  ${({ $isExpanded }) =>
    !$isExpanded &&
    ecss`
      align-items: center;
      padding: ${getSpacingSelector(MENU_PADDING_SELECTOR_SMALL)}px 0;
    `}
`
